import { useState, useRef, useEffect } from "react";
import { useInterviewContext } from "../contexts/InterviewContext";
import {
  extractTextFromPdf,
  extractTextFromDocx,
  extractTextFromDoc,
} from "../utils/fileUtils";
import {
  generateQuestions,
  getCVs,
  regenerateQuestionsFromCv,
} from "../services/interviewAssistantService";
import Loader from "./Loader";

const CVUploader = () => {
  const { setCvData, setQuestions, setIsLoading, setError, isLoading, error } =
    useInterviewContext();
  const [selectedFile, setSelectedFile] = useState(null);
  const [jobDescription, setJobDescription] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const [existingCVs, setExistingCVs] = useState([]);
  const [selectedCvSessionId, setSelectedCvSessionId] = useState("");

  useEffect(() => {
    const fetchCVs = async () => {
      try {
        const cvs = await getCVs();
        if (cvs && cvs.length > 0) {
          setExistingCVs(cvs);
        }
      } catch (err) {
        console.error("Failed to fetch existing CVs:", err);
        // Non-critical error, user can still upload a new CV.
      }
    };

    fetchCVs();
  }, []);

  const handleExistingCvSelect = (e) => {
    const sessionId = e.target.value;
    setSelectedCvSessionId(sessionId);
    if (sessionId) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      setError(null);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    if (file.size > 10 * 1024 * 1024) {
      setError("File is too large. Please upload a file smaller than 10MB.");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    const validTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];
    if (!validTypes.includes(file.type)) {
      setError(
        "Unsupported file type. Please upload a PDF or DOCX file."
      );
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }
    setSelectedFile(file);
    setError(null);
    setSelectedCvSessionId(""); // Clear dropdown selection
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => setIsDragging(false);

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) validateAndSetFile(file);
  };

  const handleDivClick = (e) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setSelectedFile(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSubmit = async () => {
    if (!selectedFile && !selectedCvSessionId) {
      return setError("Please upload a new CV or select a previous one.");
    }

    setIsLoading((prev) => ({ ...prev, cvAnalysis: true }));
    setError(null);

    try {
      if (selectedFile) {
        // Handle new CV upload - parsed completely server-side
        const analysisServiceResponse = await generateQuestions(
          selectedFile,
          jobDescription.trim() || null
        );
        const analysisData = analysisServiceResponse;

        if (!analysisData || analysisData.length === 0) {
          throw new Error(
            "CV analysis response is missing session ID or extracted information."
          );
        }

        setCvData((prev) => ({
          ...prev,
          rawText: "CV text extracted and processed server-side.",
          fileName: selectedFile.name,
          jobDescription: jobDescription.trim() || null,
          sessionId: analysisData[0].sessionId,
        }));
        setQuestions(analysisData);
      } else if (selectedCvSessionId) {
        // Handle existing CV selection
        const analysisServiceResponse = await regenerateQuestionsFromCv(
          selectedCvSessionId,
          jobDescription.trim() || null
        );
        const analysisData = analysisServiceResponse;

        if (!analysisData) {
          throw new Error(
            "CV analysis response is missing session ID or extracted information."
          );
        }

        setQuestions(analysisData);
        setCvData({
          rawText: "CV text from a previous session is not displayed.",
          fileName: 'CV from Session',
          jobDescription: jobDescription.trim() || null,
          sessionId: analysisData[0].sessionId,
        });
      }
    } catch (err) {
      console.error("CV Processing Error:", err);
      setError(
        err.message || "An unexpected error occurred during CV processing."
      );
    } finally {
      setIsLoading((prev) => ({ ...prev, cvAnalysis: false }));
    }
  };

  const getButtonText = () => {
    if (isLoading.cvAnalysis) return "Processing...";
    if (selectedFile) return "Analyze New CV";
    if (selectedCvSessionId) return "Start with Selected CV";
    return "Select a file or CV to begin";
  };

  return (
    <div className="w-full max-w-3xl mx-auto text-slate-200">
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-white mb-3">
          🚀 Prepare for Your Interview
        </h2>
        <p className="text-slate-300 text-lg">
          Upload a new CV or select a previous one to get started.
        </p>
      </div>

      <div className="space-y-8">
        {existingCVs.length > 0 && (
          <div>
            <label
              htmlFor="cv-select"
              className="block text-sm font-medium text-slate-300 mb-1.5"
            >
              Select a Previous CV
            </label>
            <select
              id="cv-select"
              value={selectedCvSessionId}
              onChange={handleExistingCvSelect}
              className="w-full rounded-xl border border-slate-600 bg-slate-700/50 shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base text-slate-200 placeholder-slate-400"
            >
              <option value="">-- Use a saved CV --</option>
              {existingCVs.map((cv) => (
                <option key={cv.id} value={cv.id}>
                  {cv.cvName}
                </option>
              ))}
            </select>
          </div>
        )}

        {existingCVs.length > 0 && (
          <div className="relative flex items-center justify-center my-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-700"></div>
            </div>
            <div className="relative bg-slate-900 px-4 text-sm font-semibold text-slate-400">
              OR
            </div>
          </div>
        )}

        <div
          className={`border-2 border-dashed rounded-xl ${
            isDragging
              ? "border-blue-500 bg-slate-700/50"
              : selectedFile
              ? "border-green-500 bg-slate-700/40"
              : "border-slate-600 hover:border-blue-500 bg-slate-800/60"
          } transition-colors duration-200 p-8 text-center cursor-pointer relative overflow-hidden`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleDivClick}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.docx"
            onChange={handleFileChange}
            className="absolute -left-full opacity-0 pointer-events-none"
            tabIndex="-1"
          />
          <div className="flex flex-col items-center justify-center space-y-3">
            <div className="bg-slate-700 rounded-full p-4">
              <svg
                className="w-10 h-10 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            {selectedFile ? (
              <div className="w-full max-w-xs">
                <p
                  className="font-semibold text-green-400 truncate text-base"
                  title={selectedFile.name}
                >
                  {selectedFile.name}
                </p>
                <p className="text-sm text-slate-400 mt-1">
                  {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </p>
                <button
                  className="text-xs text-red-400 hover:text-red-300 mt-2 hover:underline"
                  onClick={handleRemoveFile}
                >
                  Remove file
                </button>
              </div>
            ) : (
              <>
                <p className="font-medium text-slate-300 text-base">
                  Drop a new CV here or{" "}
                  <span className="text-blue-400 hover:text-blue-300">
                    browse
                  </span>
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Supported formats: PDF, DOCX (max 10MB)
                </p>
              </>
            )}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">
            Job Description (Optional)
          </label>
          <textarea
            rows="4"
            placeholder="Paste the job description here to improve CV analysis..."
            value={jobDescription}
            onChange={(e) => setJobDescription(e.target.value)}
            className="w-full rounded-xl border border-slate-600 bg-slate-700/50 shadow-sm p-3 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base text-slate-200 placeholder-slate-400"
          />
          <p className="mt-1.5 text-xs text-slate-400">
            Adding a job description helps us tailor our analysis to the
            specific role.
          </p>
        </div>

        {error && (
          <div className="bg-red-900/30 border border-red-700/50 text-red-300 px-4 py-3 rounded-lg text-sm">
            <p>{error}</p>
          </div>
        )}

        {isLoading.cvAnalysis ? (
          <div className="py-6 text-center">
            <Loader message="Parsing & analyzing your CV..." />
          </div>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={
              (!selectedFile && !selectedCvSessionId) || isLoading.cvAnalysis
            }
            className="w-full group py-3 sm:py-4 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600
              text-white font-semibold text-base hover:shadow-xl hover:from-blue-500 hover:to-purple-700
              disabled:opacity-60 disabled:cursor-not-allowed shadow-lg transition-all duration-300
              transform hover:scale-105"
          >
            {getButtonText()}
          </button>
        )}
        <div className="pt-6 border-t border-slate-700 mt-8">
          <h3 className="text-base font-semibold text-white mb-3">
            What you'll get:
          </h3>
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm text-slate-300">
            <li className="flex items-center">
              <svg
                className="w-5 h-5 text-green-400 mr-2.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
              Session-based tracking
            </li>
            <li className="flex items-center">
              <svg
                className="w-5 h-5 text-green-400 mr-2.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
              ID-based question evaluation
            </li>
            <li className="flex items-center">
              <svg
                className="w-5 h-5 text-green-400 mr-2.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
              Personalized interview questions
            </li>
            <li className="flex items-center">
              <svg
                className="w-5 h-5 text-green-400 mr-2.5 flex-shrink-0"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M5 13l4 4L19 7"
                ></path>
              </svg>
              Detailed feedback on answers
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CVUploader;
